import {
	Box,
	Button,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { capitalizeString } from "nhb-toolbox";
import { useEffect, useState } from "react";
import { Controller, useForm, type UseFormReset } from "react-hook-form";
import CommonFileUploader from "src/common/CommonFileUploader";
import { formatDateInput, isValidDateRange } from "src/Helpers/utils";
import { FAIR_STATUS } from "src/Helpers/utils/constants";
import { toast } from "src/Toaster/Toaster";
import type { IAdminFair } from "src/Types/module/fair/adminFair";
import type { IFairCreation, IFairUpdate } from "./type/fairType";

interface Props {
	onSubmit: (
		data: IFairCreation | IFairUpdate,
		reset: UseFormReset<IFairCreation | IFairUpdate>
	) => void;
	isLoading: boolean;
	previousData?: IAdminFair;
	EditMode?: boolean;
}

export default function FairForm({
	onSubmit,
	isLoading,
	previousData,
}: Props) {
	const { control, register, handleSubmit, reset, formState } = useForm<
		IFairCreation | IFairUpdate
	>({
		defaultValues: {
			fair_name: previousData?.fair_name || "",
			fair_location: previousData?.fair_location || "",
			fair_details: previousData?.fair_details || "",
			fair_status: previousData?.fair_status || "upcoming",
		},
	});

	const [startDate, setStartDate] = useState<Dayjs | null>(
		previousData?.fair_start_date
			? dayjs(previousData.fair_start_date)
			: null
	);

	const [endDate, setEndDate] = useState<Dayjs | null>(
		previousData?.fair_end_date ? dayjs(previousData.fair_end_date) : null
	);

	useEffect(() => {
		if (previousData) {
			reset({
				fair_name: previousData.fair_name || "",
				fair_location: previousData.fair_location || "",
				fair_details: previousData.fair_details || "",
				fair_status: previousData.fair_status || "upcoming",
			});
			setStartDate(
				previousData.fair_start_date
					? dayjs(previousData.fair_start_date)
					: null
			);
			setEndDate(
				previousData.fair_end_date
					? dayjs(previousData.fair_end_date)
					: null
			);
		}
	}, [previousData?.fair_id, reset]);

	useEffect(() => {
		const firstError = Object.values(formState.errors).find(
			(error) => error?.message
		);

		if (firstError?.message) {
			toast.fire({
				icon: "error",
				title: "Missing Info!",
				text: firstError.message,
				position: "top-right",
			});
		}
	}, [
		formState.errors.fair_name,
		formState.errors.fair_location,
		formState.errors.fair_main_banner,
		formState.errors.fair_venue_layout,
	]);

	const handleSubmitFair = (data: IFairCreation | IFairUpdate) => {
		if (!startDate || !endDate) {
			return toast.fire({
				icon: "warning",
				title: "Please select both start date and end date!",
				position: "top",
			});
		}

		if (!isValidDateRange(startDate, endDate)) {
			return toast.fire({
				icon: "warning",
				title: "Start date cannot be after end date!",
				position: "top",
			});
		}

		const formattedData: any = {
			...data,
			fair_start_date: formatDateInput(startDate),
			fair_end_date: formatDateInput(endDate),
		};

		// Handle fair_main_banner: extract File if selected, delete if empty
		if (formattedData.fair_main_banner instanceof FileList) {
			if (formattedData.fair_main_banner.length > 0) {
				formattedData.fair_main_banner =
					formattedData.fair_main_banner[0];
			} else {
				delete formattedData.fair_main_banner;
			}
		} else if (!formattedData.fair_main_banner) {
			delete formattedData.fair_main_banner;
		}

		// Handle fair_venue_layout: extract File if selected, delete if empty
		if (formattedData.fair_venue_layout instanceof FileList) {
			if (formattedData.fair_venue_layout.length > 0) {
				formattedData.fair_venue_layout =
					formattedData.fair_venue_layout[0];
			} else {
				delete formattedData.fair_venue_layout;
			}
		} else if (!formattedData.fair_venue_layout) {
			delete formattedData.fair_venue_layout;
		}

		onSubmit(formattedData, reset);
	};

	return (
		<Box
			component='form'
			onSubmit={handleSubmit(handleSubmitFair)}
			sx={{ p: 2, mt: 1 }}
		>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={12}>
					<TextField
						fullWidth
						type='text'
						label='Fair Name'
						{...register("fair_name", {
							required: {
								value: true,
								message: "Please provide a fair name!",
							},
						})}
					/>
				</Grid>

				<Grid item xs={12} sm={12}>
					<TextField
						fullWidth
						type='text'
						label='Location'
						{...register("fair_location", {
							required: {
								value: true,
								message: "You must provide a location!",
							},
						})}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<FormControl fullWidth required>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='Start Date'
								value={startDate}
								onChange={(newValue: Dayjs | null) => {
									setStartDate(newValue);
								}}
								slotProps={{
									textField: {
										fullWidth: true,
										InputLabelProps: { shrink: true },
									},
								}}
							/>
						</LocalizationProvider>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={6}>
					<FormControl fullWidth required>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label='End Date'
								value={endDate}
								onChange={(newValue: Dayjs | null) => {
									setEndDate(newValue);
								}}
								slotProps={{
									textField: {
										fullWidth: true,
										InputLabelProps: { shrink: true },
									},
								}}
							/>
						</LocalizationProvider>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={6}>
					<CommonFileUploader
						label='Choose Banner'
						accept='image/*'
						existingUrl={previousData?.fair_main_banner}
						{...register("fair_main_banner", {
							required: {
								value: !previousData,
								message: "Please select a banner for the fair!",
							},
						})}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<CommonFileUploader
						label='Venue Layout PDF'
						accept='application/pdf,image/*'
						existingUrl={previousData?.fair_venue_layout}
						{...register("fair_venue_layout", {
							required: {
								value: !previousData,
								message:
									"Please select a venue layout file for the fair!",
							},
						})}
					/>
				</Grid>

				{previousData && (
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth required>
							<InputLabel id='fair_status_label'>Status</InputLabel>
							<Controller
								control={control}
								name='fair_status'
								defaultValue={previousData?.fair_status}
								rules={{
									required: "Fair status is required!",
								}}
								render={({ field }) => (
									<Select
										labelId='fair_status_label'
										label='Status'
										{...field}
										value={field.value || ""}
									>
										{FAIR_STATUS.map((status) => (
											<MenuItem key={status} value={status}>
												{capitalizeString(status)}
											</MenuItem>
										))}
									</Select>
								)}
							/>
						</FormControl>
					</Grid>
				)}

				<Grid item xs={12}>
					<TextField
						fullWidth
						label='Fair Details'
						multiline
						rows={4}
						placeholder='Write additional details (optional)...'
						{...register("fair_details")}
					/>
				</Grid>
			</Grid>

			<Button
				type='submit'
				fullWidth
				variant='contained'
				sx={{ mt: 3 }}
				disabled={isLoading}
			>
				{isLoading
					? previousData
						? "Updating..."
						: "Creating..."
					: previousData
						? "Update Fair"
						: "Create Fair"}
			</Button>
		</Box>
	);
}
